import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate } from 'react-router-dom';
import { Pie } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { auth, db } from './firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import './App.css';


ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

// Login Component (unchanged)
function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/dashboard');
    } catch (err) {
      setError('Invalid credentials');
      console.error(err);
    }
  };

  return (
    <div className="App login-page">
      <div className="login-container">
        <h1>Login</h1>
        <form onSubmit={handleLogin} className="login-form">
          <div className="form-group">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <button type="submit">Login</button>
          </div>
          {error && <p className="error">{error}</p>}
        </form>
      </div>
    </div>
  );
}

// Dashboard Component (updated)
function Dashboard() {
  const [expenses, setExpenses] = useState([]);
  const [partner, setPartner] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingExpense, setEditingExpense] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');

  const partners = [
    'Sravan K',
    'Srikanth G',
    'Rajendra Ch',
    'Shreekanth C',
    'Sreevalli',
    'Ravi U',
    'Naveen K',
  ];

  const navigate = useNavigate();

  useEffect(() => {
    const fetchExpenses = async () => {
      const expensesCollection = collection(db, 'expenses');
      const snapshot = await getDocs(expensesCollection);
      const expensesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setExpenses(expensesData);
    };
    fetchExpenses();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!partner || !amount || !description || !date) return;

    const expenseData = {
      partner,
      amount: parseFloat(amount),
      description,
      date,
    };

    try {
      if (editingExpense) {
        const expenseDoc = doc(db, 'expenses', editingExpense.id);
        await updateDoc(expenseDoc, expenseData);
        setExpenses(expenses.map(exp => exp.id === editingExpense.id ? { id: exp.id, ...expenseData } : exp));
        setEditingExpense(null);
      } else {
        const docRef = await addDoc(collection(db, 'expenses'), expenseData);
        setExpenses([...expenses, { id: docRef.id, ...expenseData }]);
      }
      setPartner('');
      setAmount('');
      setDescription('');
      setDate('');
    } catch (err) {
      alert('You do not have permission to modify expenses.');
      console.error('Error saving expense:', err);
    }
  };

  const editExpense = (expense) => {
    setEditingExpense(expense);
    setPartner(expense.partner);
    setAmount(expense.amount);
    setDescription(expense.description);
    setDate(expense.date);
  };

  const removeExpense = async (id) => {
    try {
      await deleteDoc(doc(db, 'expenses', id));
      setExpenses(expenses.filter(exp => exp.id !== id));
    } catch (err) {
      alert('You do not have permission to delete expenses.');
      console.error('Error deleting expense:', err);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (err) {
      console.error('Error logging out:', err);
    }
  };



  const getTotals = () => {
    const totalBudget = 7500000; // Total project budget in INR
    const partnerShares = {
      'Sravan K': 0.20,    // 20%
      'Srikanth G': 0.25,  // 25%
      'Rajendra Ch': 0.20, // 20%
      'Shreekanth C': 0.20, // 20%
      'Sreevalli': 0.05,    // 5%
      'Ravi U': 0.05,      // 5%
      'Naveen K': 0.05    // 5%
    };
  
    const partnerTotals = expenses.reduce((acc, curr) => {
      acc[curr.partner] = (acc[curr.partner] || 0) + curr.amount;
      return acc;
    }, {});
  
    const fullTotal = expenses.reduce((acc, curr) => acc + curr.amount, 0);
  
    // Calculate allocated budget, spent, and remaining for each partner
    const totalsWithBudget = Object.keys(partnerShares).reduce((acc, partner) => {
      const allocated = totalBudget * partnerShares[partner];
      const spent = partnerTotals[partner] || 0;
      acc[partner] = {
        allocated,
        spent,
        remaining: allocated - spent
      };
      return acc;
    }, {});
  
    return { totalsWithBudget, partnerTotals, fullTotal };
  };


  const filteredExpenses = expenses.filter((exp) =>
    exp.partner.toLowerCase().includes(searchQuery.toLowerCase()) ||
    exp.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedExpenses = [...filteredExpenses].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const totals = getTotals();
  const pieData = {    
    labels: Object.keys(totals.partnerTotals).length > 0 ? Object.keys(totals.partnerTotals) : partners,
    datasets: [
      {
        label: 'Capital Spent (₹)',
        data: Object.keys(totals.partnerTotals).length > 0 ? Object.values(totals.partnerTotals) : partners.map(() => 0),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
          'rgba(255, 159, 64, 0.6)',
          'rgba(199, 199, 199, 0.6)',
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(199, 199, 199, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const pieOptions = {
    plugins: {
      legend: { position: 'right' },
      title: { display: true, text: 'Capital Expenditure' },
      datalabels: {
        display: true,
        color: '#333',
        font: { size: 12, weight: 'bold' },
        formatter: (value, context) => {
          const total = context.dataset.data.reduce((acc, val) => acc + val, 0);
          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
          return `${context.chart.data.labels[context.dataIndex]}\n${percentage}%`;
        },
        // align: 'end',
        // anchor: 'end',
        offset: 15,
        clamp: true,
        clip: false,
      },
    },
    maintainAspectRatio: true,
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="App">
      <div className="header">
        <h1>Capital Tracker - Dashboard</h1>
        <button onClick={logout} className="logout-btn">Logout</button>
      </div>
      <div className="form-container">
        <form onSubmit={handleSubmit} className="expense-form">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            max={today}
            required
          />
          <select value={partner} onChange={(e) => setPartner(e.target.value)} required>
            <option value="" disabled>Select Partner</option>
            {partners.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <input type="number" placeholder="Amount (₹)" value={amount} onChange={(e) => setAmount(e.target.value)} step="0.01" />
          <input type="text" placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
          {/* <textarea
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows="3"
          /> */}
          <button type="submit" className="add-btn">{editingExpense ? 'Update' : 'Add'} Expense</button>
          {editingExpense && (
            <button type="button" onClick={() => setEditingExpense(null)}>Cancel</button>
          )}
        </form>
      </div>
      <hr className="divider" />
      <div className="content">
        <div className="dashboard-layout">

          <div className="left-column">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search by Partner or Description"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="expenses-list">
              {sortedExpenses.length === 0 ? (
                <p>No expenses recorded yet.</p>
              ) : (
                <>
                  <p className="table-total">
                    Total: ₹ {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
                      sortedExpenses.reduce((acc, exp) => acc + exp.amount, 0)
                    )}
                  </p>
                  <table>
                    <thead>
                      <tr>
                        <th onClick={toggleSortOrder} style={{ cursor: 'pointer' }}>
                          Date {sortOrder === 'asc' ? '↑' : '↓'}
                        </th>
                        <th>Partner</th>
                        <th>Amount</th>
                        <th>Description</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedExpenses.map((exp) => (
                        <tr key={exp.id}>
                          <td>
                            {new Intl.DateTimeFormat('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            }).format(new Date(exp.date))}
                          </td>
                          <td>{exp.partner}</td>
                          <td>
                            ₹ {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(exp.amount)}
                          </td>
                          <td>{exp.description}</td>
                          <td>
                            <button onClick={() => editExpense(exp)} className="edit-btn">✏️</button>
                            <button onClick={() => removeExpense(exp.id)} className="remove-btn">🗑️</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>




          <div className="right-column">
            <div className="chart">
              <Pie data={pieData} options={pieOptions} height={300} />
            </div>
            <div className="totals">
              {Object.keys(totals.totalsWithBudget).length > 0 ? (
                <>
                  <table className="totals-table">
                    <thead>
                      <tr>
                        <th>Partner</th>
                        <th className='tAmount'>Spent</th>
                        <th className='tAmount'>Pending</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(totals.totalsWithBudget).map(([partner, { allocated, spent, remaining }]) => (
                        <tr key={partner}>
                          <td>
                            <strong>{partner}</strong>
                            <sub style={{color:"blue"}}> (₹ {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(allocated)})</sub>
                          </td>
                          <td className='tAmount'>₹ {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(spent)}</td>
                          <td className='tAmount'>₹ {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(remaining)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <p>No totals to display.</p>
              )}
            </div>
          </div>













        </div>
      </div>
    </div>
  );
}

// Cleared Component (unchanged)
function Cleared() {
  const navigate = useNavigate();

  const logout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (err) {
      console.error('Error logging out:', err);
    }
  };

  return (
    <div className="App">
      <div className="header">
        <h1>Data Cleared</h1>
        <button onClick={logout} className="logout-btn">Logout</button>
      </div>
      <div className="content">
        <p>All expenses have been cleared from the database.</p>
        <Link to="/dashboard">Back to Dashboard</Link>
      </div>
    </div>
  );
}

// Protected Route Component (unchanged)
function ProtectedRoute({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) return <div>Loading...</div>;
  return user ? children : <Navigate to="/login" replace />;
}

// Main App with Routing (unchanged)
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />
        <Route
          path="/dashboard"
          element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
        />
        <Route
          path="/cleared"
          element={<ProtectedRoute><Cleared /></ProtectedRoute>}
        />
      </Routes>
    </Router>
  );
}

export default App;